import {createSelector} from 'reselect'
import get from 'lodash/get.js'

import {ensureArray} from '../../common/ensure.js'
import {
  allOrdersSelector,
  getOrderNumbersThatCanHaveShippingLabels,
  getOrderNumbersWithShippingLabels,
  getOrderNumbersThatCanHaveReturnLabels,
  getOrderNumbersWithReturnLabels,
  getIsContentsVerified,
  ensureOrdersLoaded,
} from '../../data/orders.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import apiverson from '../../common/apiverson.js'
import {PACK_SHIP_PAGE} from '../../common/constants/Pages.js'
import {showPackingOrderModal} from './Modals/PackingOrderModal.js'
import {formsSelector, getState, updateForm} from '../../store.js'
import {navigate} from '../../common/location.js'
import {LABEL_TYPE_SHIPPING} from '../../common/constants/LabelConfig.js'

export const PACK_SHIP_PAGE_FORM = 'pack_ship_page_form'
export const PACK_SHIP_URI_COMPONENT = 'pack_ship'
export const PACK_SHIP_ORDER_NOT_FOUND = 'PACK_SHIP_ORDER_NOT_FOUND'
export const PACK_SHIP_GENERAL_ERROR = 'PACK_SHIP_GENERAL_ERROR'

export function packShipPageFormSelector(state) {
  return formsSelector(state)[PACK_SHIP_PAGE_FORM]
}

export const packShipOrderNumbersSelector = createSelector(
  locationSelector,
  ({query}) => ensureArray(query.orderNumbers),
)

export const indexSelector = createSelector(
  locationSelector,
  ({query}) => Number(query.index) || 0,
)

export const packShipErrorSelector = createSelector(
  packShipPageFormSelector,
  ({error} = {}) => error,
)

export const packShipIsLoadingSelector = createSelector(
  packShipPageFormSelector,
  ({isLoading} = {}) => isLoading === true,
)

export const packShipLabelTypeSelector = createSelector(
  packShipPageFormSelector,
  ({labelType} = {}) => labelType || LABEL_TYPE_SHIPPING,
)

export const currentPackShipOrderNumberSelector = createSelector(
  packShipOrderNumbersSelector,
  indexSelector,
  (orderNumbers, index) => orderNumbers[index],
)

export const currentPackShipOrderSelector = createSelector(
  currentPackShipOrderNumberSelector,
  allOrdersSelector,
  (orderNumber, orders) => get(orders, orderNumber, null),
)

export const hashParamsSelector = createSelector(
  packShipOrderNumbersSelector,
  indexSelector,
  (orderNumbers, index) => ({
    index: index > 0 ? index : undefined,
    orderNumbers: orderNumbers.length ? orderNumbers : undefined,
  }),
)

export const backParamsSelector = createSelector(
  hashParamsSelector,
  indexSelector,
  (params, index) => {
    if (index < 1) {
      return null
    }

    return {
      ...params,
      index: index - 1,
    }
  },
)

export const nextParamsSelector = createSelector(
  hashParamsSelector,
  packShipOrderNumbersSelector,
  indexSelector,
  (params, orderNumbers, index) => {
    if (index >= orderNumbers.length - 1) {
      return null
    }

    return {
      ...params,
      index: index + 1,
    }
  },
)

export const currentPackShipOrderNeedsContentsVerifiedSelector = createSelector(
  currentPackShipOrderSelector,
  (order) => !getIsContentsVerified(order),
)

export const verifiedOrderCountSelector = createSelector(
  packShipOrderNumbersSelector,
  allOrdersSelector,
  (orderNumbers, orders) =>
    orderNumbers.reduce((prev, orderNumber) => {
      const order = orders[orderNumber]

      if (getIsContentsVerified(order)) {
        return prev + 1
      }

      return prev
    }, 0),
)

export const currentPackShipOrderNeedsLabelSelector = createSelector(
  currentPackShipOrderSelector,
  packShipLabelTypeSelector,
  (order, labelType) => {
    if (!order) {
      return false
    }

    const ordersThatCan =
      labelType === LABEL_TYPE_SHIPPING
        ? getOrderNumbersThatCanHaveShippingLabels([order], [])
        : getOrderNumbersThatCanHaveReturnLabels([order], [])

    return ordersThatCan.length === 1
  },
)

export const currentPackShipOrderNeedsToPrintSelector = createSelector(
  currentPackShipOrderSelector,
  packShipLabelTypeSelector,
  (order, labelType) => {
    if (!order) {
      return false
    }

    const ordersThatNeed =
      labelType === LABEL_TYPE_SHIPPING
        ? getOrderNumbersWithShippingLabels([order], [])
        : getOrderNumbersWithReturnLabels([order], [])

    return ordersThatNeed.length === 1
  },
)

export function setupPackShipPageForm() {
  return {
    formName: PACK_SHIP_PAGE_FORM,
    initialForm: {
      error: null,
      isLoading: true,
      labelType: LABEL_TYPE_SHIPPING,
    },
  }
}

export function updatePackShipPageForm(updates, meta) {
  updateForm(PACK_SHIP_PAGE_FORM, updates, meta)
}

export function navigateToPackShip(orderNumbers) {
  const params = orderNumbers ? {orderNumbers} : null

  return navigate([PACK_SHIP_URI_COMPONENT], params)
}

export function setPackShipError(type, message = null) {
  updatePackShipPageForm({
    error: type
      ? {
          type,
          message,
        }
      : null,
  })
}

export function packShipPageDoneLoading() {
  updatePackShipPageForm({
    isLoading: false,
  })
}

export function setPackShipLabelType(labelType) {
  updatePackShipPageForm({
    labelType,
  })
}

export async function setupPackShipPage() {
  const orderNumbers = packShipOrderNumbersSelector(getState())

  await ensureOrdersLoaded(orderNumbers)

  packShipPageDoneLoading()
}

export async function packShipOrderScanned(scannedValue) {
  setPackShipError(null)

  try {
    const {json} = await apiverson.get('/order/', {
      barcode: scannedValue,
      limit: 1,
      offset: 0,
      status: 'all',
    })

    if (json.count !== 1) {
      setPackShipError(PACK_SHIP_ORDER_NOT_FOUND)
    } else {
      const order = json.order[0]
      const currentPage = currentPageSelector(getState())

      if (currentPage === PACK_SHIP_PAGE) {
        navigateToPackShip([order.order_number])
      } else {
        showPackingOrderModal(order.order_number)
      }
    }
  } catch (error) {
    setPackShipError(PACK_SHIP_GENERAL_ERROR, error.message)
  }
}
